import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Chip,
    Tooltip
} from "@mui/material";
import SlidePanel from "@/theme/partials/slidepanel";
import Icon from "@/theme/vectors/bundle";
import { useTranslation } from "@/hooks/useTranslation";
import { useTableData } from "@/hooks/database/useTableData";
import { useTableDataEditor } from "@/hooks/database/useTableDataEditor";
import { useTableDataFilter } from "@/hooks/database/useTableDataFilter";
import { isUnixTimestamp, formatTimestamp } from "@/utils/database/formatters";

// Format operator for display
const formatOperator = (op, t) => {
    switch(op) {
        case 'contains': return t('database.filter.operators.contains');
        case 'equals': return t('database.filter.operators.equals');
        case 'startsWith': return t('database.filter.operators.startsWith');
        case 'endsWith': return t('database.filter.operators.endsWith');
        case 'greaterThan': return t('database.filter.operators.greaterThan');
        case 'lessThan': return t('database.filter.operators.lessThan');
        default: return op;
    }
};

// Filter Modal Component
const FilterModal = ({ 
    open, 
    onClose, 
    availableColumns, 
    filterConditions, 
    addFilterCondition, 
    removeFilterCondition, 
    updateFilterCondition,
    applyFilters,
    clearFilters
}) => {
    const { t } = useTranslation();
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <Icon name="filter" size={20} />
                    <span>{t('database.filter.title')}</span>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('database.filter.description')}
                    </Typography>
                    
                    {filterConditions.length === 0 ? (
                        <Typography color="text.secondary">
                            {t('database.filter.noConditions')}
                        </Typography>
                    ) : (
                        <Box>
                            {filterConditions.map((condition, index) => (
                                <Grid container spacing={2} key={index} alignItems="center" mb={2}>
                                    <Grid item xs={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('database.filter.column')}</InputLabel>
                                            <Select
                                                value={condition.column}
                                                label={t('database.filter.column')}
                                                onChange={(e) => updateFilterCondition(index, 'column', e.target.value)}
                                            >
                                                {availableColumns.map((column) => (
                                                    <MenuItem key={column} value={column}>{column}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('database.filter.operator')}</InputLabel>
                                            <Select
                                                value={condition.operator}
                                                label={t('database.filter.operator')}
                                                onChange={(e) => updateFilterCondition(index, 'operator', e.target.value)}
                                            >
                                                <MenuItem value="contains">{t('database.filter.operators.contains')}</MenuItem>
                                                <MenuItem value="equals">{t('database.filter.operators.equals')}</MenuItem>
                                                <MenuItem value="startsWith">{t('database.filter.operators.startsWith')}</MenuItem>
                                                <MenuItem value="endsWith">{t('database.filter.operators.endsWith')}</MenuItem>
                                                <MenuItem value="greaterThan">{t('database.filter.operators.greaterThan')}</MenuItem>
                                                <MenuItem value="lessThan">{t('database.filter.operators.lessThan')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t('database.filter.value')}
                                            value={condition.value}
                                            onChange={(e) => updateFilterCondition(index, 'value', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <IconButton 
                                            color="error"
                                            onClick={() => removeFilterCondition(index)}
                                        >
                                            <Icon name="remove" size={20} />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                    )}
                </Box>
                
                <Button
                    variant="outlined"
                    startIcon={<Icon name="add" size={16} />}
                    onClick={addFilterCondition}
                    disabled={availableColumns.length === 0}
                >
                    {t('database.filter.addCondition')}
                </Button>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={clearFilters} 
                    color="secondary"
                    disabled={filterConditions.length === 0}
                    startIcon={<Icon name="remove" size={16} />}
                >
                    {t('database.filter.clearFilters')}
                </Button>
                <Button 
                    onClick={onClose} 
                    color="primary"
                    startIcon={<Icon name="close" size={16} />}
                >
                    {t('common.cancel')}
                </Button>
                <Button 
                    onClick={applyFilters} 
                    color="primary" 
                    variant="contained"
                    startIcon={<Icon name="filter" size={16} />}
                    disabled={filterConditions.length === 0}
                >
                    {t('database.filter.applyFilters')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TableViewPanel = ({ tableName, isOpen, onClose }) => {
    const { t } = useTranslation();
    
    // Table data
    const { 
        tableData, 
        filteredTableData,
        setFilteredTableData,
        loadingTableData, 
        tableDataError,
        fetchTableData,
        liveRefresh,
        toggleLiveRefresh
    } = useTableData(tableName, isOpen);
    
    // Table editing
    const {
        editingCell,
        editValue,
        editInputRef,
        handleCellClick,
        handleSaveEdit,
        handleCancelEdit,
        handleEditKeyPress,
        setEditValue
    } = useTableDataEditor(tableName, tableData, filteredTableData);
    
    // Filtering
    const {
        filterModalOpen,
        filterConditions,
        availableColumns,
        openFilterModal,
        closeFilterModal,
        addFilterCondition,
        removeFilterCondition,
        updateFilterCondition,
        applyFilters,
        clearFilters
    } = useTableDataFilter(tableData, filteredTableData, setFilteredTableData);
    
    // Clean up when panel closes or component unmounts
    useEffect(() => {
        return () => {
            if (editingCell) {
                handleCancelEdit();
            }
        };
    }, [isOpen, editingCell, handleCancelEdit]);
    
    // Render table data
    const renderTableData = () => {
        if (!filteredTableData || filteredTableData.length === 0) {
            return (
                <Typography>
                    {filterConditions.length > 0 
                        ? t('database.tableView.noMatchingData') 
                        : t('database.tableView.noData')}
                </Typography>
            );
        }
        
        // Get all columns
        const columns = Object.keys(filteredTableData[0]);
        
        return (
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map(column => (
                                <TableCell key={column}>{column}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTableData.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {columns.map(column => {
                                    const isEditing = editingCell && 
                                        editingCell.rowIndex === rowIndex && 
                                        editingCell.columnName === column;
                                    
                                    const cellValue = row[column] !== undefined ? String(row[column]) : null;
                                    
                                    return (
                                        <TableCell 
                                            key={column} 
                                            onClick={() => !isEditing && handleCellClick(rowIndex, column, cellValue)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                                        >
                                            {isEditing ? (
                                                <Box position="relative">
                                                    <TextField
                                                        ref={editInputRef}
                                                        autoFocus
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onKeyDown={handleEditKeyPress}
                                                        size="small"
                                                        fullWidth
                                                        variant="outlined"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <Box display="flex" gap={0.5}>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={handleSaveEdit}
                                                                        color="primary"
                                                                    >
                                                                        <Icon name="check" size={16} />
                                                                    </IconButton>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={handleCancelEdit}
                                                                        color="error"
                                                                    >
                                                                        <Icon name="close" size={16} />
                                                                    </IconButton>
                                                                </Box>
                                                            )
                                                        }}
                                                    />
                                                </Box>
                                            ) : isUnixTimestamp(cellValue) ? (
                                                <Tooltip title={formatTimestamp(cellValue)} arrow placement="top">
                                                    <span>{cellValue}</span>
                                                </Tooltip>
                                            ) : (
                                                cellValue
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };
    
    return (
        <>
            <SlidePanel 
                title={tableName ? `${t('database.tableView.title')}: ${tableName}` : t('database.tableView.defaultTitle')} 
                isOpen={isOpen} 
                onClose={onClose}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" color="text.secondary">
                        {filterConditions.length > 0 
                            ? t('database.tableView.filteredData', { filtered: filteredTableData.length, total: tableData.length }) 
                            : t('database.tableView.showingRecords')}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={() => fetchTableData(tableName)} 
                            disabled={loadingTableData}
                            startIcon={<Icon name="refresh2" size={20} />}
                            size="small"
                        >
                            {t('common.refresh')}
                        </Button>
                        <Button 
                            variant={liveRefresh ? "contained" : "outlined"}
                            color="primary"
                            onClick={toggleLiveRefresh}
                            disabled={loadingTableData}
                            startIcon={<Icon name="halfDottedCirclePlay" size={20} />}
                            size="small"
                        >
                            {t('database.tableView.live')}
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="primary" 
                            onClick={openFilterModal}
                            disabled={loadingTableData || tableData.length === 0}
                            startIcon={<Icon name="filter" size={20} />}
                            size="small"
                        >
                            {t('database.tableView.filter')}
                        </Button>
                    </Box>
                </Box>

                {loadingTableData ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : tableDataError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {t('database.tableView.loadError')}
                    </Alert>
                ) : (
                    <>
                        {filterConditions.length > 0 && (
                            <Box 
                                sx={{ 
                                    mt: 1, 
                                    mb: 2, 
                                    p: 1.5, 
                                    bgcolor: 'rgba(0, 127, 255, 0.08)', 
                                    borderRadius: 1,
                                    border: '1px solid rgba(0, 127, 255, 0.2)'
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Icon name="filter" size={18} />
                                    <Typography variant="subtitle2">{t('database.filter.applied')}:</Typography>
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {filterConditions.map((condition, index) => (
                                        <Chip
                                            key={index}
                                            label={`${condition.column} ${formatOperator(condition.operator, t)} "${condition.value}"`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            onDelete={() => {
                                                removeFilterCondition(index);
                                                if (filterConditions.length === 1) {
                                                    setFilteredTableData(tableData); // Reset when removing last filter
                                                }
                                            }}
                                        />
                                    ))}
                                    <Chip
                                        label={t('database.filter.clearAll')}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        onClick={clearFilters}
                                    />
                                </Box>
                            </Box>
                        )}
                        
                        {renderTableData()}
                    </>
                )}
            </SlidePanel>
            
            {/* Filter Modal */}
            <FilterModal
                open={filterModalOpen}
                onClose={closeFilterModal}
                availableColumns={availableColumns}
                filterConditions={filterConditions}
                addFilterCondition={addFilterCondition}
                removeFilterCondition={removeFilterCondition}
                updateFilterCondition={updateFilterCondition}
                applyFilters={applyFilters}
                clearFilters={clearFilters}
            />
        </>
    );
};

export default TableViewPanel;